/* eslint-disable no-nested-ternary */
/* eslint-disable no-console */
/* eslint-disable react/jsx-no-undef */
import { ViewIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Center,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tooltip,
  useBoolean,
  useDisclosure,
  useToast,
  Text,
  Image,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { useQuery } from 'react-query';
import { Api } from 'api';
import { DataTable, LoadingIndicator } from 'components';
import React, { useState } from 'react';
import {
  InquiryTramerData,
  ProductType,
  ProductTypeQueryCacheKey,
  TramerDamageDateKeyResponse,
  TramerDamageResponseData,
  TramerQueryType,
  ExpertisePointGroupTypeLabel,
} from 'types';

export interface AdminExpertisePointTramerInquiriesHistoryTableProps {
  inquiriesData: InquiryTramerData[];
}

export const AdminExpertisePointTramerInquiriesHistoryTable: React.FC<
  AdminExpertisePointTramerInquiriesHistoryTableProps
> = ({ inquiriesData }) => {
  const toast = useToast();

  const { isOpen: isConfirmDialogOpen, onOpen: onConfirmDialogOpen, onClose: onConfirmDialogClose } = useDisclosure();
  const [currentTramerData, setCurrentTramerData] = useState<InquiryTramerData | undefined>();
  const [currentDamageDateKey, setCurrentDamageDateKey] = useState<string>();
  const [isDamageDataKeyLoading, { on: onDamageDataKeyLoading, off: offDamageDataKeyLoading }] = useBoolean();
  const [changedPart, setChangedPart] = useState<TramerDamageResponseData | null>(null);
  const {
    isOpen: isDamageDateKeyDialogOpen,
    onOpen: onDamageDateKeyDialogOpen,
    onClose: onDamageDateKeyDialogClose,
  } = useDisclosure();

  const columnHelper = createColumnHelper<InquiryTramerData>();

  const columns = [
    columnHelper.accessor('chassisNumber', {
      header: 'Şase no',
    }),

    columnHelper.accessor('groupType', {
      header: 'Marka',
      cell: (info) => {
        const groupType = info.getValue();
        return groupType ? ExpertisePointGroupTypeLabel.get(groupType) || '-' : '-';
      },
    }),

    columnHelper.accessor('createdAt', {
      header: 'Tarih',
      cell: (info) => new Date(info.getValue()).toLocaleString('tr-TR'),
    }),

    columnHelper.accessor('queryTypeId', {
      header: 'Sorgu Tipi',
      cell: (info) => {
        if (!info.getValue()) return '-';
        if (info.getValue() === TramerQueryType.Damage) {
          return 'Hasar Sorgu';
        }
        if (info.getValue() === TramerQueryType.ChangedPart) {
          return 'Değişen Parça';
        }
        if (info.getValue() === TramerQueryType.Tramer360Chassis) {
          return 'Araç Detay Sorgu (Tramer360)';
        }
        return 'Araç Detay';
      },
    }),

    columnHelper.accessor('url', {
      header: 'URL',
      cell: (info) => {
        return (
          <Flex gap={4}>
            <Tooltip label="Görüntüle">
              <IconButton
                aria-label="Görüntüle"
                icon={<ViewIcon />}
                onClick={() => {
                  const tramerResult = info.row.original;
                  let convertedTramerResult = (
                    tramerResult.damageDateKeys ? JSON.parse(tramerResult.damageDateKeys) : []
                  ) as TramerDamageDateKeyResponse[];

                  if (typeof convertedTramerResult === 'string') {
                    convertedTramerResult = JSON.parse(convertedTramerResult);
                  }

                  tramerResult.damageDateKeysArr = convertedTramerResult
                    .filter(
                      (damageDateKey: TramerDamageDateKeyResponse) =>
                        damageDateKey.value !== '' || damageDateKey.damageDateKey !== '',
                    )
                    .map(
                      (damageDateKey: TramerDamageDateKeyResponse) =>
                        ({
                          orderId: damageDateKey.orderId,
                          value: damageDateKey.value ?? damageDateKey.damageDateKey,
                        } as TramerDamageDateKeyResponse),
                    );

                  setCurrentTramerData(tramerResult);
                }}
              />
            </Tooltip>
          </Flex>
        );
      },
    }),
  ];

  const onDamageDateKeyClicked = (damageDateKey: string) => {
    setCurrentDamageDateKey(damageDateKey);
    onConfirmDialogOpen();
  };

  const { data: TramerResultProductPrices } = useQuery(
    ProductTypeQueryCacheKey.GetTramerQuery,
    async () => {
      const response = await Api.Util.getProductPriceByType(ProductType.TramerQuery);
      return response.data;
    },
    {
      refetchOnWindowFocus: false,
      cacheTime: 0,
      onError: (error: any) => {
        const {
          response: {
            data: { message },
          },
        } = error;
        toast({
          title: message,
          status: 'error',
          isClosable: true,
        });
      },
    },
  );

  const fetchDamageDateDate = async (damageDateKey: string) => {
    onDamageDataKeyLoading();
    try {
      const response = await Api.Integration.getTramerChangedPartByChassis(
        currentTramerData?.chassisNumber ?? '',
        damageDateKey,
      );
      const damageDateData = response.data.tramerResult;
      setChangedPart(damageDateData);
    } catch (err) {
      onDamageDateKeyDialogClose();
      console.error(err);
      toast({
        title: 'Parça Değişen Bilgileri Çekilirken Hata Oluştu',
        isClosable: true,
        status: 'error',
      });
    } finally {
      offDamageDataKeyLoading();
    }
  };

  const onDamageDateKeyConfirmedClicked = async () => {
    if (currentDamageDateKey) {
      await fetchDamageDateDate(currentDamageDateKey);
      onDamageDateKeyDialogOpen();
    }
    onConfirmDialogClose();
  };

  return (
    <>
      <DataTable
        data={inquiriesData}
        columns={columns}
        usePagination
        tableProps={{
          variant: 'striped',
          colorScheme: 'teal',
        }}
        title="Tramer Sorgulama Geçmişi"
      />
      {currentTramerData && (
        <Modal isOpen={currentTramerData != null} isCentered size="5xl" onClose={() => setCurrentTramerData(undefined)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Hasar Kaydı Sorgula</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Center>
                <div
                  style={{
                    backgroundImage: `url(${currentTramerData?.url})`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    position: 'relative',
                    width: '700px',
                    height: '700px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {currentTramerData &&
                    currentTramerData.damageDateKeysArr.map((damageDateKey: TramerDamageDateKeyResponse) => {
                      return (
                        <div
                          key={damageDateKey.orderId}
                          style={{
                            flexDirection: 'column',
                            display: 'flex',
                            position: 'absolute',
                            width: '100%',
                            left: '20%',
                            top: '10%',
                            marginTop: `${damageDateKey.orderId * 33}px`,
                          }}
                        >
                          {damageDateKey.value && (
                            <Button
                              variant="unstyled"
                              style={{
                                position: 'relative',
                                width: '200px',
                                marginLeft: 'auto',
                                marginRight: 'auto',
                                color: 'blue',
                              }}
                              onClick={() => {
                                onDamageDateKeyClicked(damageDateKey.value);
                              }}
                              colorScheme="blue"
                            >
                              Değişen Sorgu
                            </Button>
                          )}
                        </div>
                      );
                    })}
                </div>
              </Center>
            </ModalBody>
            <ModalFooter>
              <Button onClick={() => setCurrentTramerData(undefined)}>Kapat</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      <Modal
        isOpen={isDamageDateKeyDialogOpen}
        onClose={() => {
          // refetchDamageDateKeySet();
          onDamageDateKeyDialogClose();
        }}
        isCentered
        size="4xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Araç Değişen Sorgu</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Center>
              {isDamageDataKeyLoading || !changedPart ? (
                <LoadingIndicator />
              ) : (
                <Box maxHeight="80vh" maxWidth="100%" overflowY="auto" marginTop="2rem" padding="1rem">
                  <Image src={changedPart.url} boxSizing="border-box" width="100%" height="auto" />
                </Box>
              )}
            </Center>
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={() => {
                onDamageDateKeyDialogClose();
              }}
            >
              Kapat
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isConfirmDialogOpen}
        onClose={onConfirmDialogClose}
        isCentered
        closeOnOverlayClick={!isDamageDataKeyLoading}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Araç Değişen Sorgu Onay?</ModalHeader>
          <ModalCloseButton disabled={isDamageDataKeyLoading} />
          <ModalBody>
            {TramerResultProductPrices && (
              <Text>
                Hasar Kaydı Sorgusu için ₺
                {TramerResultProductPrices.buyPrice > 0
                  ? TramerResultProductPrices.buyPrice
                  : TramerResultProductPrices.price}{' '}
                sorgu bedeli kullanılacaktır. Devam etmek istiyor musunuz ?
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={onDamageDateKeyConfirmedClicked}
              isLoading={isDamageDataKeyLoading}
            >
              Evet
            </Button>
            <Button onClick={onConfirmDialogClose} disabled={isDamageDataKeyLoading}>
              Hayır
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
